import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Menu } from "lucide-react";
import ConfirmationDialog from "../../../Components/ConfirmationDialogs/ConfirmationDialogs";
import UploadExcel from "../../../Components/UploadExcel";
import { normalizeRowKeys } from "../../../utils/case";
import { slugify } from "../../../utils/slug";
import SearchBar from "../../../Components/DataView/Searchbar";
import Pagination from "../../../Components/DataView/Pagination";
import { useFirestorePagination } from "../../../hooks/useFirestorePagination";
import { useSearchBar } from "../../../hooks/useSearchBar";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useDelete } from "../../../hooks/useDelete";

/* ---------- types and helper functions (unchanged) ---------- */
interface CourseListing {
  ID?: string;
  courseName: string;
  universityName: string;
  universityLocation?: string;
  duration?: string;
  intake?: string[];
  tuitionFee?: number;
  deals?: string[];
  scholarships?: any[];
  fieldOfStudy?: string;
  major?: string;
  level?: string;
  paymentSchemes?: string;
  partTimeOption?: string;
  abroadDuration?: string;
  country?: string;
  state?: string;
  overseasTransfer?: string;
  courseSlug?: string;
  uniSlug?: string;
  majorSlug?: string;
  fieldOfStudySlug?: string;
  registrationFee?: number;
  entryRequirements?: {
    qualification: string;
    grade?: string;
    details?: string;
  }[];
  englishRequirements?: {
    title: string;
    details?: string;
  }[];
}

function parseIntake(value: unknown): string[] {
  if (Array.isArray(value))
    return value.map((v) => String(v).trim()).filter(Boolean);
  const str = String(value ?? "");
  if (!str.trim()) return [];
  return str
    .split(/[;,]/g)
    .map((v) => v.trim())
    .filter(Boolean);
}

function parseEntryRequirements(
  value: unknown,
): { qualification: string; grade?: string; details?: string }[] {
  const text = String(value ?? "").trim();
  if (!text) return [];
  return text
    .split(/;\s*/)
    .map((part) => {
      const match = part.match(/^([^:]+):\s*([^-\n]+)?-?\s*(.*)$/);
      if (!match) return { qualification: part.trim() };
      const [, qualification, grade, details] = match;
      return {
        qualification: qualification?.trim() || "",
        grade: grade?.trim() || "",
        details: details?.trim() || "",
      };
    })
    .filter((r) => r.qualification);
}

function parseEnglishRequirements(
  value: unknown,
): { title: string; details?: string }[] {
  if (!value) return [];
  const str = String(value);
  return str
    .split(";")
    .map((item) => {
      const [title, details] = item.split(":").map((v) => v.trim());
      return { title: title || "", details: details || "" };
    })
    .filter((obj) => obj.title);
}

function transformRow(rawRow: Record<string, any>): CourseListing | null {
  const row = normalizeRowKeys(rawRow);
  const courseName = String(row.courseName ?? "").trim();
  const universityName = String(row.universityName ?? "").trim();
  const level = String(row.level ?? "").trim();
  if (!courseName || !universityName) return null;

  return {
    courseName,
    universityName,
    universityLocation: String(row.universityLocation ?? "").trim(),
    duration: String(row.duration ?? "").trim(),
    intake: parseIntake(row.intake),
    tuitionFee: Number(row.tuitionFee ?? 0),
    deals: parseIntake(row.deals),
    scholarships: parseIntake(row.scholarships),
    fieldOfStudy: String(row.fieldOfStudy ?? "").trim(),
    major: String(row.major ?? "").trim(),
    level,
    paymentSchemes: String(row.paymentSchemes ?? "").trim(),
    partTimeOption: String(row.partTimeOption ?? "").trim(),
    abroadDuration: String(row.abroadDuration ?? "").trim(),
    country: String(row.country ?? "").trim(),
    state: String(row.state ?? "").trim(),
    overseasTransfer: String(row.overseasTransfer ?? "").trim(),
    courseSlug: slugify(`${courseName}-${universityName}`),
    uniSlug: slugify(`${universityName}`),
    majorSlug: slugify(`${row.major}`),
    fieldOfStudySlug: slugify(`${row.fieldOfStudy}`),
    registrationFee: Number(row.registrationFee ?? 0),
    englishRequirements: parseEnglishRequirements(row.englishRequirements),
    entryRequirements: parseEntryRequirements(row.entryRequirements),
  };
}

function buildCourseDedupKey(data: Record<string, any>): string {
  const d = normalizeRowKeys(data);
  const parts = [
    String(d.courseName ?? "")
      .toLowerCase()
      .trim(),
    String(d.universityName ?? "")
      .toLowerCase()
      .trim(),
    String(d.level ?? "")
      .toLowerCase()
      .trim(),
  ];
  if (!parts[0] || !parts[1]) return "";
  return parts.join("|");
}
function isDuplicate(row: CourseListing, existingDocs: Map<string, any>) {
  const key = buildCourseDedupKey(row);
  if (!key) return false;
  return existingDocs.has(key);
}
function getDeduplicationKey(row: Record<string, any>): string {
  return buildCourseDedupKey(row);
}

/* ----------------- Component ----------------- */
export default function CourseListing() {
  const navigate = useNavigate();
  const { userRole } = useAuthUser();

  // Sorting state
  const [sortField, setSortField] = useState("courseName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Search mode state
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Memoize empty objects/arrays to prevent infinite loop in useFirestorePagination
  // because default arguments in the hook ([], {}) create new references on every render
  const baseConstraints = useMemo(() => [], []);
  const filters = useMemo(() => ({}), []);

  // Pagination & Data Hook
  const {
    items: currentCourses,
    currentPage,
    totalPages,
    showPage,
    refresh,
    setIsLoading, // consumed by SearchBar via onLoading
    setItems,
    setTotalDocs,
    setTotalPages,
    setCurrentPage,
  } = useFirestorePagination<CourseListing>({
    collectionName: "course-listing",
    itemsPerPage: 20,
    sortField,
    sortOrder,
    isSearchMode,
    baseConstraints,
    filters,
  });

  // Search Bar Hook
  const {
    searchBarRef,
    handleSearchResults,
    onLoading: onSearchLoading,
  } = useSearchBar(
    {
      setItems,
      setTotalDocs,
      setTotalPages,
      setCurrentPage,
      setIsLoading,
    },
    20,
    isSearchMode,
    setIsSearchMode,
  );

  // UI state
  const [dropdownCourseId, setDropdownCourseId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  /* ----------------- scroll-to-top after page change ----------------- */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  /* ----------------- click outside dropdown ----------------- */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-dropdown-menu]")) {
        setDropdownCourseId(null);
        setDropdownPosition(null);
        setDropdownAnchor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ----------------- Actions ----------------- */
  const handleAddCourse = () => navigate("/course-listing/add-course");

  const {
    showDeleteDialog,
    confirmDelete,
    cancelDelete,
    handleDelete: handleDeleteCourse,
    itemToDelete: courseToDelete,
  } = useDelete<CourseListing>({
    collectionName: "course-listing",
    itemNameField: "courseName",
    onSuccess: refresh,
  });

  /* ----------------- Sorting header click ----------------- */
  const handleSortHeaderClick = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="relative flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Course Listing</h2>
        <div className="flex gap-4">
          <UploadExcel
            collectionName="course-listing"
            transformRow={transformRow}
            isDuplicate={isDuplicate}
            getDeduplicationKey={getDeduplicationKey}
            buttonLabel="Upload Courses"
          />
          <button
            onClick={handleAddCourse}
            className="w-32 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700"
          >
            Add Course
          </button>
        </div>
      </div>

      <SearchBar
        ref={searchBarRef}
        placeholder="Search course or university name..."
        collectionName="course-listing"
        searchFields={["courseSlug", "uniSlug"]}
        onResults={handleSearchResults}
        onLoading={onSearchLoading}
      />

      {/* Table */}
      <div className="relative min-h-[80%] overflow-x-auto border rounded mb-4">
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="bg-gray-300 sticky top-0 z-10">
              <th className="p-1">No</th>
              {[
                { label: "Course Name", field: "courseName" },
                { label: "Field of Study", field: "fieldOfStudy" },
                { label: "Major", field: "major" },
                { label: "Level", field: "level" },
                { label: "University Name", field: "universityName" },
                { label: "Duration", field: "duration" },
                { label: "Intake", field: "intake" },
                { label: "Tuition Fee", field: "tuitionFee" },
                { label: "Country", field: "country" },
                { label: "State", field: "state" },
                { label: "Scholarships", field: "scholarships" },
              ].map(({ label, field }) => (
                <th
                  key={field}
                  className="p-1 cursor-pointer select-none"
                  onClick={() => handleSortHeaderClick(field)}
                >
                  {label}
                  {sortField === field && (
                    <span className="ml-1">
                      {sortOrder === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              ))}
              <th className="p-1">Action</th>
            </tr>
          </thead>

          <tbody>
            {!currentCourses || currentCourses.length === 0 ? (
              <tr>
                <td colSpan={13} className="p-2">
                  No course listings found.
                </td>
              </tr>
            ) : (
              currentCourses.map((course, index) => (
                <tr
                  key={course.ID || index}
                  className="hover:bg-gray-100 border-b"
                >
                  <td className="p-1">{(currentPage - 1) * 20 + index + 1}</td>
                  {[
                    course.courseName,
                    course.fieldOfStudy,
                    course.major,
                    course.level,
                    course.universityName,
                    course.duration ? `${course.duration} years` : "",
                    Array.isArray(course.intake)
                      ? course.intake.join("; ")
                      : "",
                    course.tuitionFee ?? "",
                    course.country ?? "",
                    course.state ?? "",
                    Array.isArray(course.scholarships)
                      ? course.scholarships
                          .map((s) =>
                            typeof s === "object" && s !== null
                              ? `${s.title || ""}`
                              : String(s),
                          )
                          .join("; ")
                      : "",
                  ].map((val, i) => (
                    <td key={i} className="p-1">
                      {val}
                    </td>
                  ))}
                  <td className="p-1">
                    <button
                      onClick={(e) => {
                        const el = e.currentTarget as HTMLElement;
                        const rect = el.getBoundingClientRect();
                        setDropdownCourseId(course.ID || null);
                        setDropdownAnchor(el);
                        setDropdownPosition({
                          top: rect.bottom,
                          left: rect.left,
                        });
                      }}
                      className="text-gray-700 hover:text-black focus:outline-none"
                      title="Actions"
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => showPage(page)}
      />

      {/* Action Dropdown */}
      {dropdownCourseId && dropdownPosition && (
        <div
          data-dropdown-menu
          className="fixed z-10 w-32 bg-white border rounded shadow"
          style={{
            top: dropdownPosition.top - 30,
            left: dropdownPosition.left - 140,
          }}
        >
          <button
            onClick={() => {
              navigate(`/course-listing/edit-course/${dropdownCourseId}`);
              setDropdownCourseId(null);
              setDropdownPosition(null);
              setDropdownAnchor(null);
            }}
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Edit
          </button>
          {userRole === "super admin" && (
            <button
              onClick={() => {
                const found = currentCourses.find(
                  (c) => c.ID === dropdownCourseId,
                );
                if (found) {
                  confirmDelete(found);
                }
                setDropdownCourseId(null);
                setDropdownPosition(null);
                setDropdownAnchor(null);
              }}
              className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
            >
              Delete
            </button>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        icon={<Trash2 size={32} className="text-red-600" />}
        title="Delete Course"
        message={`Are you sure you want to delete ${courseToDelete?.courseName}? This action cannot be undone.`}
        onCancel={cancelDelete}
        onConfirm={handleDeleteCourse}
      />
    </div>
  );
}
